import { useState, useEffect } from 'react';
import { getLandingPageConfig } from '@app/rangeConfig/landingPageConfig';
import type { LandingPageConfig } from '@app/rangeConfig/@types';

/**
 * Lightweight hook for loading landing page configuration.
 * This hook dynamically loads only the landing page config (images, colors, etc.),
 * without the heavy gallery data which is loaded separately via `useGalleryConfig`.
 *
 * This enables tree-shaking to work properly by avoiding static imports of gallery configs.
 *
 * @returns Landing page configuration object
 * @returns null while loading
 *
 * @example
 * ```typescript
 * const config = useLandingPageConfig();
 *
 * if (!config) {
 *   return <LoadingSpinner />;
 * }
 *
 * const { rangeDesign, designFromScratch, openDesign, guide } = config;
 * ```
 */
export const useLandingPageConfig = (): LandingPageConfig | null => {
  const [config, setConfig] = useState<LandingPageConfig | null>(null);

  useEffect(() => {
    let cancelled = false;

    const loadConfig = async () => {
      try {
        const landingPageConfig = await getLandingPageConfig();

        if (!cancelled) {
          setConfig(landingPageConfig);
        }
      } catch {
        if (!cancelled) {
          // Set minimal fallback config to prevent infinite loading
          setConfig({
            rangeDesign: {},
            designFromScratch: { backgroundColor: '#F5F5F5' },
            openDesign: { backgroundColor: '#F5F5F5' },
            browseInspiration: {}
          });
        }
      }
    };

    loadConfig();

    return () => {
      cancelled = true;
    };
  }, []);

  return config;
};
